import React from 'react';
import { useAtomValue } from 'jotai';
import { tileSliderAtom } from '@rocco/states/tileSliderAtom';
import type {
    TileViewModel,
    TileableModel,
} from '@rocco/ui/tile-shared/view-models/tile';
import { isProductTileViewModel } from '@rocco/ui/tile-product/view-models/product';
import { ProductTile } from '@rocco/ui/tile-product/components/ProductTile';
import { isArticleTileViewModel } from '@rocco/ui/tile-article/view-models/article';
import { ArticleTile } from '@rocco/ui/tile-article/components/ArticleTile';
import {
    type ProfessionalTileViewModel,
    isProfessionalTileViewModel,
} from '@rocco/ui/tile-professional/view-models/professional';
import { ProfessionalTile } from '@rocco/ui/tile-professional/components/ProfessionalTile';
import { isProjectTileViewModel } from '@rocco/ui/tile-project/view-models/project';
import { ProjectTile } from '@rocco/ui/tile-project/components/ProjectTile';
import {
    type CategoryTileViewModel,
    isCategoryTileViewModel,
} from '@rocco/ui/tile-category/view-models/category';
import { CategoryTile } from '@rocco/ui/tile-category/components/CategoryTile';
import { AdminTile } from './AdminTile';
import { StaticTileViewModel } from '@rocco/ui/tile-static/view-models/static';
import { StaticTile } from '@rocco/ui/tile-static/components/StaticTile';
import {
    type WebsiteTileViewModel,
    isWebsiteTileViewModel,
} from '@rocco/ui/tile-website/view-models/website';
import { WebsiteTile } from '@rocco/ui/tile-website/components/WebsiteTile';

interface TileProps {
    tile: TileViewModel<TileableModel>;
    showTypeBadge?: boolean;
    preventScrollReset?: boolean;
    adminActions?: {
        onEdit: () => void;
        onRepositionImage: () => void;
        onDelete?: () => void;
        onTogglePin?: () => void;
    };
    isSelected?: boolean;
    inlineActionButtons?: React.ReactNode;
}

/**
 * The Tile component is the main component for rendering any type of tile in the application.
 * It takes a TileViewModel with a TileableModel and determines the specific tile component to render based on the model type.
 * This component serves as an adaptor to specific tiles, keeping the logic simple and focused on rendering the correct tile.
 */
export const Tile = ({
    tile,
    showTypeBadge,
    preventScrollReset,
    adminActions,
    isSelected,
    inlineActionButtons,
}: TileProps) => {
    const {
        enableTileSliderAll,
        enableTileSliderProduct,
        enableTileSliderProject,
        disableTileSliderABTest,
    } = useAtomValue(tileSliderAtom);

    const renderTile = (children: React.ReactNode) => {
        return (
            <AdminTile tile={tile} actions={adminActions}>
                {children}
            </AdminTile>
        );
    };

    if (isProductTileViewModel(tile.model)) {
        return renderTile(
            <ProductTile
                {...tile}
                model={
                    showTypeBadge
                        ? {
                              ...tile.model,
                              badge: tile.model.badge || 'Product',
                          }
                        : tile.model
                }
                inlineActionButtons={inlineActionButtons}
                enableTileSlider={
                    enableTileSliderAll && enableTileSliderProduct
                }
                disableTileSliderABTest={disableTileSliderABTest}
            />
        );
    }

    if (isProfessionalTileViewModel(tile.model)) {
        return renderTile(
            <ProfessionalTile
                {...(tile as TileViewModel<ProfessionalTileViewModel>)}
                inlineActionButtons={inlineActionButtons}
                enableTileSlider={enableTileSliderAll}
                disableTileSliderABTest={disableTileSliderABTest}
            />
        );
    }

    if (isProjectTileViewModel(tile.model)) {
        return renderTile(
            <ProjectTile
                {...tile}
                model={
                    showTypeBadge
                        ? {
                              ...tile.model,
                              badge: tile.model.badge || 'Project',
                          }
                        : tile.model
                }
                inlineActionButtons={inlineActionButtons}
                enableTileSlider={
                    enableTileSliderAll && enableTileSliderProject
                }
                disableTileSliderABTest={disableTileSliderABTest}
            />
        );
    }

    if (isArticleTileViewModel(tile.model)) {
        return renderTile(
            <ArticleTile
                {...tile}
                model={
                    showTypeBadge
                        ? {
                              ...tile.model,
                              badge: tile.model.badge || 'Article',
                          }
                        : tile.model
                }
                inlineActionButtons={inlineActionButtons}
                enableTileSlider={enableTileSliderAll}
                disableTileSliderABTest={disableTileSliderABTest}
            />
        );
    }

    if (isCategoryTileViewModel(tile.model)) {
        return (
            <CategoryTile
                {...(tile as TileViewModel<CategoryTileViewModel>)}
                preventScrollReset={preventScrollReset}
                isSelected={isSelected}
            />
        );
    }

    if (isWebsiteTileViewModel(tile.model)) {
        return renderTile(
            <WebsiteTile
                {...(tile as TileViewModel<WebsiteTileViewModel>)}
                preventScrollReset={preventScrollReset}
                isSelected={isSelected}
                inlineActionButtons={inlineActionButtons}
            />
        );
    }

    return (
        <StaticTile
            {...(tile as TileViewModel<StaticTileViewModel>)}
            preventScrollReset={preventScrollReset}
            isSelected={isSelected}
            inlineActionButtons={inlineActionButtons}
        />
    );
};
